import { useTranslation } from 'react-i18next';

/**
 * Paging for a list.
 *
 * Replaces a "first page / load more" pair, which could only ever move forwards and never told
 * you where you were. Three things a list of 3 300 names actually needs:
 *
 *   - **Where am I, out of how many.** Both the row range and the page count, because "1–50 of
 *     3 304" and "page 1 of 67" answer different questions and both get asked.
 *   - **Jump.** Numbered pages with ellipses, so page 40 is one click and coming back to it is
 *     another. A cursor cannot express that at all, which is why the API grew page numbers
 *     alongside its cursors rather than instead of them.
 *   - **How many at a time.** Someone scanning wants 200 rows; someone reading wants 25.
 *
 * The size control lives here rather than in each screen so every list behaves the same way,
 * and the chosen size is a URL parameter like everything else — a reload keeps it.
 *
 * The words come from the catalogue. They were written in French directly in the markup, which
 * meant an English-speaking coordinator got an English application with a French pager in it.
 */

export const PAGE_SIZES = [25, 50, 100, 200] as const;

type Props = {
  page: number;
  pageCount: number;
  pageSize: number;
  total: number;
  /** Rows on the current page, so the range reads correctly on a short last page. */
  count: number;
  onPage: (page: number) => void;
  onPageSize: (size: number) => void;
  /** Plural noun for the total, e.g. "clients". Already in the reader's language. */
  label: string;
  busy?: boolean;
  /**
   * The sizes offered. Overridable because "how many fit on a screen" is a property of the row,
   * not of the pager: the journal's entries are three lines each, an invoice is one.
   */
  sizes?: readonly number[];
};

export function Pager({ page, pageCount, pageSize, total, count, onPage, onPageSize, label, busy, sizes = PAGE_SIZES }: Props) {
  const { t: translate, i18n } = useTranslation();
  const t = translate as (key: string, vars?: Record<string, string | number>) => string;
  const intl = i18n.language === 'en' ? 'en-CA' : 'fr-CA';

  const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const to = total === 0 ? 0 : from + count - 1;

  return (
    <nav className="pager" aria-label={t('list.pagination', { label })}>
      <span className="pager__range">
        <span className="tnum">
          {from}–{to}
        </span>{' '}
        {label === '' ? '' : `${t('list.rangeOf')} `}
        <span className="tnum">{total.toLocaleString(intl)}</span> {label}
      </span>

      <div className="pager__pages">
        <button
          type="button"
          className="pager__step"
          onClick={() => onPage(page - 1)}
          disabled={page <= 1 || busy}
          aria-label={t('list.previousPage')}
        >
          ‹
        </button>

        {pageNumbers(page, pageCount).map((n, i) =>
          n === null ? (
            // A gap, not a button. Marked as presentational so a screen reader does not
            // announce a meaningless "…" between two page links.
            <span key={`gap-${i}`} className="pager__gap" aria-hidden="true">
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              className={`pager__page${n === page ? ' pager__page--on' : ''}`}
              onClick={() => onPage(n)}
              disabled={busy}
              aria-current={n === page ? 'page' : undefined}
              aria-label={t('list.pageNumber', { page: n })}
            >
              <span className="tnum">{n}</span>
            </button>
          ),
        )}

        <button
          type="button"
          className="pager__step"
          onClick={() => onPage(page + 1)}
          disabled={page >= pageCount || busy}
          aria-label={t('list.nextPage')}
        >
          ›
        </button>
      </div>

      <label className="pager__size">
        {t('list.perPage')}
        <select
          value={pageSize}
          onChange={(e) => onPageSize(Number(e.target.value))}
          aria-label={t('list.resultsPerPage')}
        >
          {sizes.map((size) => (
            <option key={size} value={size}>
              {size}
            </option>
          ))}
        </select>
      </label>
    </nav>
  );
}

/**
 * Which page numbers to show: always the first, the last, and a window around the current one.
 *
 * `null` marks a gap. Below nine pages every number fits, so nothing is elided — the ellipsis
 * only earns its place once the row would otherwise be too wide to scan.
 */
function pageNumbers(page: number, pageCount: number): Array<number | null> {
  if (pageCount <= 9) return Array.from({ length: pageCount }, (_, i) => i + 1);

  const out: Array<number | null> = [1];
  const from = Math.max(2, page - 2);
  const to = Math.min(pageCount - 1, page + 2);

  if (from > 2) out.push(null);
  for (let n = from; n <= to; n += 1) out.push(n);
  if (to < pageCount - 1) out.push(null);

  out.push(pageCount);
  return out;
}
